/**
 * SnapIndicator — Iter 145 visual confirmation that the operator is
 * snapped to a vertex / endpoint / calibration anchor.
 *
 * Renders inside the SheetCanvas inner SVG coordinate space (viewBox =
 * raster pixel dims) so it positions exactly under the snap target at
 * any zoom level. View-scale-aware sizing keeps the ring + crosshair
 * + label readable from 25%–800%.
 *
 * Inputs (all normalized 0..1):
 *   snap     {nx, ny, kind, label} | null
 *   imgDims  {w, h}                       raster natural dimensions
 *   viewScale number                      current canvas zoom multiplier
 *
 * When snap is null, renders nothing.
 *
 * No props ever expose internal CSS keyframes externally; the gentle
 * "pulse" is a CSS animation defined locally so it cannot collide with
 * the rest of the app.
 */
import React from "react";

const ORANGE = "var(--fop-primary)";

export default function SnapIndicator({ snap, imgDims, viewScale }) {
  if (!snap) return null;
  const W = imgDims?.w || 1;
  const H = imgDims?.h || 1;
  const x = snap.nx * W;
  const y = snap.ny * H;
  const vs = Math.max(0.05, viewScale || 1);
  // All sizes in raster-px so they scale with the canvas viewBox.
  const ringOuter = 22 / vs;
  const ringInner = 14 / vs;
  const ringStroke = 2.5 / vs;
  const tickLen = 18 / vs;
  const tickStroke = 1.5 / vs;
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      data-testid="snap-indicator-svg"
    >
      <g data-testid="snap-indicator" data-snap-kind={snap.kind}>
        <style>{`@keyframes fop-snap-pulse {
          0%   { opacity: 0.55; transform: scale(0.92); }
          50%  { opacity: 1.0;  transform: scale(1.06); }
          100% { opacity: 0.55; transform: scale(0.92); }
        }`}</style>
        {/* Animated outer ring */}
        <g style={{ transformOrigin: `${x}px ${y}px`, transformBox: "fill-box", animation: "fop-snap-pulse 0.9s ease-in-out infinite" }}>
          <circle
            cx={x} cy={y} r={ringOuter}
            fill="none"
            stroke={ORANGE}
            strokeWidth={ringStroke}
            vectorEffect="non-scaling-stroke"
          />
        </g>
        {/* Inner static ring + white halo for contrast on light AND dark drawings */}
        <circle cx={x} cy={y} r={ringInner} fill="none" stroke="#fff" strokeWidth={ringStroke + 1 / vs} strokeOpacity="0.85" vectorEffect="non-scaling-stroke" />
        <circle cx={x} cy={y} r={ringInner} fill="none" stroke={ORANGE} strokeWidth={ringStroke} vectorEffect="non-scaling-stroke" />
        {/* CAD-style crosshair confirming exact landing coordinate */}
        <line x1={x - tickLen} y1={y} x2={x - 4 / vs} y2={y} stroke={ORANGE} strokeWidth={tickStroke} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
        <line x1={x + 4 / vs} y1={y} x2={x + tickLen} y2={y} stroke={ORANGE} strokeWidth={tickStroke} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
        <line x1={x} y1={y - tickLen} x2={x} y2={y - 4 / vs} stroke={ORANGE} strokeWidth={tickStroke} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
        <line x1={x} y1={y + 4 / vs} x2={x} y2={y + tickLen} stroke={ORANGE} strokeWidth={tickStroke} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
        {/* Bright center dot */}
        <circle cx={x} cy={y} r={3 / vs} fill={ORANGE} stroke="#fff" strokeWidth={1 / vs} vectorEffect="non-scaling-stroke" />
      </g>
    </svg>
  );
}
